import { useEffect, useState } from "react";
import axios from "axios";
import { CalendarCheck, Eye, Mail, Users } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StatCard = ({ icon: Icon, label, value, sub, testId }) => (
  <div data-testid={testId} className="rounded-3xl border border-black/10 bg-white p-7">
    <div className="flex items-center justify-between">
      <p className="text-xs font-semibold uppercase tracking-[0.18em] text-zinc-500">{label}</p>
      <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-pine/10">
        <Icon className="h-4 w-4 text-pine" />
      </span>
    </div>
    <p className="mt-4 font-serif text-4xl tracking-tight text-ink">{value}</p>
    {sub && <p className="mt-1 text-xs text-zinc-500">{sub}</p>}
  </div>
);

export const AdminAnalytics = () => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/admin/analytics?days=30`, { withCredentials: true })
      .then((r) => setData(r.data))
      .catch(() => setError(true));
  }, []);

  if (error) {
    return (
      <div data-testid="analytics-error" className="mt-6 rounded-3xl border border-black/10 bg-white p-10 text-sm text-zinc-600">
        Could not load analytics right now. Try the Refresh button above.
      </div>
    );
  }

  if (!data) {
    return (
      <div className="mt-6 flex justify-center rounded-3xl border border-black/10 bg-white p-16">
        <Loader2Safe />
      </div>
    );
  }

  const conversions = data.leads + data.bookings;
  const rate = data.visitors > 0 ? Math.round((conversions / data.visitors) * 100) : 0;
  const maxViews = Math.max(1, ...data.by_path.map((p) => p.views));

  return (
    <div data-testid="analytics-panel" className="mt-6 space-y-6">
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard icon={Users} label="Visitors · 30 days" value={data.visitors} testId="analytics-visitors" />
        <StatCard icon={Eye} label="Page views · 30 days" value={data.pageviews} testId="analytics-pageviews" />
        <StatCard icon={Mail} label="Consultation requests" value={data.leads} testId="analytics-leads" />
        <StatCard
          icon={CalendarCheck}
          label="Booking requests"
          value={data.bookings}
          sub={data.visitors > 0 ? `${rate}% of visitors sent a request` : undefined}
          testId="analytics-bookings"
        />
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <div className="rounded-3xl border border-black/10 bg-white p-8">
          <h3 className="font-serif text-xl tracking-tight text-ink">Top pages</h3>
          {data.by_path.length === 0 ? (
            <p data-testid="analytics-pages-empty" className="mt-4 text-sm text-zinc-500">
              No visits recorded yet — counting starts the moment this feature is live on your site.
            </p>
          ) : (
            <ul data-testid="analytics-top-pages" className="mt-5 space-y-3.5">
              {data.by_path.map((p) => (
                <li key={p.path} className="text-sm">
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-ink">{p.path === "/" ? "Home (/)" : p.path}</span>
                    <span className="text-zinc-500">{p.views} views</span>
                  </div>
                  <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-paper">
                    <div className="h-full rounded-full bg-pine" style={{ width: `${(p.views / maxViews) * 100}%` }} />
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="rounded-3xl border border-black/10 bg-white p-8">
          <h3 className="font-serif text-xl tracking-tight text-ink">Where visitors come from</h3>
          {data.referrers.length === 0 ? (
            <p data-testid="analytics-referrers-empty" className="mt-4 text-sm text-zinc-500">
              No outside sources yet — early visits are usually direct. As Google and your map listing
              start sending people, they will appear here.
            </p>
          ) : (
            <ul data-testid="analytics-referrers" className="mt-5 space-y-3">
              {data.referrers.map((r) => (
                <li key={r.source} className="flex items-center justify-between text-sm">
                  <span className="font-medium text-ink">{r.source}</span>
                  <span className="text-zinc-500">{r.count} visits</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <p className="text-xs text-zinc-500">
        Privacy-friendly first-party counting: no cookies, no advertising trackers, nothing tied to a
        visitor's identity. Figures cover the last {data.days} days.
      </p>
    </div>
  );
};

const Loader2Safe = () => (
  <span className="inline-flex h-6 w-6 animate-spin rounded-full border-2 border-pine/30 border-t-pine" />
);
